<template>
    <div data-test-view="goals-cycles-details">
        <l-card-content>
            <!-- Header -->
            <l-headline
                :title="cycle.name"
                :sub-title="labelVisibility"
                :sub-title-icon="`fal fa-eye`"
                :tabs="tabs"
            />

            <l-confirmation-modal
                v-if="removeWarning"
                :text="$t('goals.cycle.details.labelRemoveWarning')"
                @confirm="disableGoalCycle()"
                @cancel="removeWarning = false"
            />

            <!-- Description -->
            <div v-show="cycle.description">
                <p>{{ cycle.description }}</p>

                <hr />
            </div>
            <!--/Description -->

            <!-- Details -->
            <div class="row">
                <div class="col-md-12">
                    <l-goal-cycle-timeline
                        :plan="new Date(cycle.timeline.plan.date)"
                        :active="new Date(cycle.timeline.active.date)"
                        :end="new Date(cycle.timeline.end.date)"
                        show-progress
                    />
                    <div
                        v-show="dateIsPast(cycle.timeline.plan.date)"
                        class="clickable"
                        tabindex="0"
                        @click="showGoals()"
                        @keyup.enter="showGoals()"
                    >
                        <hr />
                        <div class="row">
                            <div class="col-md-2 text-center">
                                <h2>{{ cycle.progress }}%</h2>
                                <h4>{{ $t(`goals.cycle.details.labelProgress`) }}</h4>
                            </div>
                            <div class="col-md-2 text-center">
                                <h2>{{ cycle.goals.draft }}</h2>
                                <h4>{{ $t(`goals.cycle.details.labelDraft`) }}</h4>
                            </div>
                            <div class="col-md-2 text-center">
                                <h2>{{ cycle.goals.approvalPending }}</h2>
                                <h4>{{ $t(`goals.cycle.details.labelPendingApproval`) }}</h4>
                            </div>
                            <div class="col-md-2 text-center color-primary">
                                <h2>{{ cycle.goals.live }}</h2>
                                <h4>{{ $t(`goals.cycle.details.labelAll`) }}</h4>
                            </div>
                            <div class="col-md-2 text-center color-success">
                                <h2>{{ cycle.goals.onTrack }}</h2>
                                <h4>{{ $t(`goals.cycle.details.labelOnTrack`) }}</h4>
                            </div>
                            <div class="col-md-2 text-center color-danger">
                                <h2>{{ cycle.goals.offTrack }}</h2>
                                <h4>{{ $t(`goals.cycle.details.labelOffTrack`) }}</h4>
                            </div>
                        </div>
                    </div>

                    <div
                        v-show="
                            cycle.settings.freezeGoalsWhenActive ||
                                cycle.settings.archiveGoalsWhenEnd
                        "
                    >
                        <hr />
                        <div v-show="cycle.settings.freezeGoalsWhenActive">
                            <i class="fal fa-info-circle"></i>
                            <span class="l-default-margin-s">{{
                                $t(`goals.cycle.details.settings.freezeGoalsWhenActive`)
                            }}</span>
                        </div>

                        <div v-show="cycle.settings.archiveGoalsWhenEnd">
                            <i class="fal fa-info-circle"></i>
                            <span class="l-default-margin-s">{{
                                $t(`goals.cycle.details.settings.archiveGoalsWhenEnd`)
                            }}</span>
                        </div>
                    </div>

                    <div
                        v-show="
                            user.rights.G_CYCLE_ADMIN &&
                                cycle.defaults &&
                                cycle.defaults.tags &&
                                cycle.defaults.tags.length
                        "
                    >
                        <hr />
                        <i class="fal fa-tag"></i>
                        <span class="l-default-margin-s">{{ $t(`goals.cycle.details.tags`) }}</span
                        >:
                        <span
                            v-for="tag in cycle.defaults.tags"
                            :key="tag._id"
                            class="color-grey l-default-margin-s"
                        >
                            {{ tag.name }}
                        </span>
                    </div>

                    <div v-show="cycle.participants.all">
                        <hr />
                        <i class="fal fa-users"></i>
                        <span class="l-default-margin-s">{{
                            $t(`goals.cycle.details.allParticipants`)
                        }}</span>
                    </div>

                    <div v-show="cycle.participants.users && cycle.participants.users.length">
                        <hr />
                        <i class="fal fa-user"></i>
                        <span class="l-default-margin-s">{{
                            $t(`goals.cycle.details.usersParticipants`)
                        }}</span>
                        <br />
                        <div class="row">
                            <div
                                v-for="user in cycle.participants.users"
                                :key="user._id"
                                class="col-md-4"
                            >
                                <div class="card">
                                    <div
                                        class="card-body clickable"
                                        style="padding: 15px"
                                        tabindex="0"
                                        @click="createGoalForUser(user)"
                                        @keyup.enter="createGoalForUser(user)"
                                    >
                                        <l-profile-picture :user="user" size="s" />
                                        <span class="color-grey l-default-margin-s">
                                            {{ user.firstname }} {{ user.lastname }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-show="cycle.participants.teams && cycle.participants.teams.length">
                        <hr />
                        <i class="fal fa-users"></i>
                        <span class="l-default-margin-s">{{
                            $t(`goals.cycle.details.teamsParticipants`)
                        }}</span>
                        <br />
                        <div class="row">
                            <div
                                v-for="team in cycle.participants.teams"
                                :key="team._id"
                                class="col-md-4"
                            >
                                <div class="card">
                                    <div
                                        class="card-body clickable"
                                        style="padding: 15px"
                                        tabindex="0"
                                        @click="createGoalForTeam(team)"
                                        @keyup.enter="createGoalForTeam(team)"
                                    >
                                        <l-profile-picture
                                            :user="{ name: team.name, picture: teamPicture }"
                                            size="s"
                                        />
                                        <span class="color-grey l-default-margin-s">{{
                                            team.name
                                        }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-show="dateIsPast(cycle.timeline.plan.date)">
                        <div v-show="cycle.usersWithoutGoals.length">
                            <hr />
                            <i class="fal fa-flag"></i>
                            <span class="l-default-margin-s">{{
                                $t(`goals.cycle.details.usersWithoutGoals`)
                            }}</span>
                            <span
                                v-if="
                                    cycle.usersWithoutGoals &&
                                        cycle.participants &&
                                        cycle.participants.users &&
                                        cycle.participants.users.length
                                "
                                >(
                                {{
                                    (
                                        (cycle.usersWithoutGoals.length /
                                            cycle.participants.users.length) *
                                        100
                                    ).toFixed(2)
                                }}
                                %)
                            </span>

                            <l-space :size="1" />

                            <div class="row">
                                <div
                                    v-for="user in cycle.usersWithoutGoals.slice(
                                        0,
                                        maxUsersWithoutGoals,
                                    )"
                                    :key="user._id"
                                    class="col-md-4"
                                >
                                    <div class="card">
                                        <div
                                            class="card-body clickable"
                                            style="padding: 15px"
                                            tabindex="0"
                                            @click="createGoalForUser(user)"
                                            @keyup.enter="createGoalForUser(user)"
                                        >
                                            <l-profile-picture :user="user" size="s" />
                                            <span class="color-grey l-default-margin-s">
                                                {{ user.firstname }} {{ user.lastname }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    v-show="cycle.usersWithoutGoals.length > maxUsersWithoutGoals"
                                    class="col-md-4 clickable"
                                    tabindex="0"
                                    @click="
                                        () => {
                                            maxUsersWithoutGoals = 1000;
                                        }
                                    "
                                    @keyup.enter="
                                        () => {
                                            maxUsersWithoutGoals = 1000;
                                        }
                                    "
                                >
                                    <div class="card">
                                        <div class="card-body" style="padding: 15px">
                                            <span class="color-grey">{{
                                                $t(`goals.cycle.details.showMoreUsers`, {
                                                    c:
                                                        cycle.usersWithoutGoals.length -
                                                        maxUsersWithoutGoals,
                                                })
                                            }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-show="cycle.teamsWithoutGoals.length">
                            <hr />
                            <i class="fal fa-flag"></i>
                            <span class="l-default-margin-s"
                                >{{ $t(`goals.cycle.details.teamsWithoutGoals`) }}
                            </span>
                            <span
                                v-if="
                                    cycle.participants &&
                                        cycle.participants.teams &&
                                        cycle.teamsWithoutGoals &&
                                        cycle.teamsWithoutGoals.length
                                "
                                >(
                                {{
                                    (
                                        ((cycle.participants.teams.length || 0) /
                                            cycle.teamsWithoutGoals.length) *
                                        100
                                    ).toFixed(2)
                                }}
                                %)</span
                            >

                            <l-space :size="1" />

                            <div class="row">
                                <div
                                    v-for="team in cycle.teamsWithoutGoals"
                                    :key="team._id"
                                    class="col-md-4"
                                >
                                    <div class="card">
                                        <div
                                            class="card-body clickable"
                                            style="padding: 15px"
                                            tabindex="0"
                                            @click="createGoalForTeam(team)"
                                            @keyup.enter="createGoalForTeam(team)"
                                        >
                                            <l-profile-picture
                                                :user="{ name: team.name, picture: teamPicture }"
                                                size="s"
                                            />
                                            <span class="color-grey l-default-margin-s">{{
                                                team.name
                                            }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- /Details -->
        </l-card-content>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import dateIsPast from "@/utils/date-is-past";

import LCardContent from "@/components/LCardContent";
import LGoalCycleTimeline from "@/components/LGoalCycleTimeline";
import LHeadline from "@/components/LHeadline";
import LProfilePicture from "@/components/LProfilePicture";
import LSpace from "@/components/LSpace";

import LConfirmationModal from "@/components/LConfirmationModal";

import teamPicture from "@/img/icons/team-icon.png";

export default {
    components: {
        LCardContent,
        LGoalCycleTimeline,
        LHeadline,
        LProfilePicture,
        LSpace,
        LConfirmationModal,
    },
    props: {
        id: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            cycle: {},
            removeWarning: false,
            maxUsersWithoutGoals: 12,
        };
    },
    computed: {
        ...mapGetters({
            user: "users/current/user",
            getCycleDetails: "goals/cycles/details",
        }),
        labelVisibility() {
            if (this.user.rights.G_CYCLE_ADMIN) {
                return this.$t(`goals.cycle.details.labelVisAdmin`);
            }
            if (!this.user.rights.G_CYCLE_ADMIN && this.user.teamRole.hasDirectReports) {
                return this.$t(`goals.cycle.details.labelVisManager`);
            }
            if (!this.user.rights.G_CYCLE_ADMIN && !this.user.teamRole.hasDirectReports) {
                return this.$t(`goals.cycle.details.labelVisUser`);
            }

            return "";
        },
        tabs() {
            return [
                {
                    label: this.$t("goals.cycle.details.btnDisable"),
                    show: this.user.rights.G_CYCLE_ADMIN,
                    path: ``,
                    handler: () => {
                        this.removeWarning = true;
                    },
                    testId: "disable-cycle",
                },

                {
                    tabClass: "space",
                    show: true,
                },
                {
                    label: this.$t("goals.cycle.details.btnEdit"),
                    show: this.user.rights.G_CYCLE_ADMIN,
                    handler: this.editCycle,
                    testId: "edit-cycle",
                    isButton: true,
                },
            ];
        },
        parseProgressData() {
            const pruned_data = [];
            const data = this.cycle.avg_progress || [];
            for (let i = 0; i < data.length; i++) {
                if (
                    i < data.length - 1 &&
                    this.$moment(data[i].created_at).format("YYYY-MM-DD") ===
                        this.$moment(data[i + 1].created_at).format("YYYY-MM-DD")
                ) {
                    continue;
                }
                pruned_data.push(data[i]);
            }
            return pruned_data.map(update => ({
                t: update.created_at,
                y: update.newValue,
            }));
        },
    },
    created() {
        this.cycle = this.getCycleDetails(this.id);

        this.teamPicture = teamPicture;

        // In some cases, when being redirected from create or update, the new screen keeps the scroll position
        // when what we actually want is for the scroll to be up to the top of the page again.
        //
        // We might move this behaviour to a more generic place in the future if we find this requirement to be
        // more widespread.
        //
        this.scrollToTop();
    },
    methods: {
        editCycle() {
            this.$router.push(`/goals/cycles/edit/${this.id}`);
        },
        disableGoalCycle() {
            this.$store
                .dispatch({ type: "goals/cycles/disable", payload: { id: this.id } })
                .then(() => {
                    this.$router.push(`/goals/cycles/list`);
                });
        },

        showGoals() {
            this.$router.push(`/goals/list/all?cycles=${this.id}`);
        },
        createGoalForUser(user) {
            const goal = {
                user,
                type: "goalUser",
                cycle: {
                    _id: this.id,
                },
            };

            this.$emit("create", goal);
        },
        createGoalForTeam(team) {
            const goal = {
                localTeam: team,
                managedBy: {
                    _id: this.user._id,
                    firstname: this.user.firstname,
                    lastname: this.user.lastname,
                },
                type: "goalLocalTeam",
                cycle: {
                    _id: this.id,
                },
            };

            this.$emit("create", goal);
        },

        scrollToTop() {
            // FIXME - Not supported in IE11 and Safari
            // (cf: https://caniuse.com/#feat=css-scroll-behavior)
            //
            window.scrollTo({ top: 0, behavior: "smooth" });
        },

        // Simple proxy to the utils/date-is-past function to be used in the template
        //
        dateIsPast(date) {
            return dateIsPast(date);
        },
    },
};
</script>

<style scoped></style>
